// A task's status, with its colours - the one place for these rules, used by the diary list, the
// task cards, the dashboard and the legend that explains them.
//
//   Overdue    red      due before today (a week, month position or season: its last day before today, as in Excel)
//   Due soon   orange   due today or within 2 days
//   This week  yellow   due within 7 days, or today is in the planned week, month position or season
//   Later      blue     due more than 7 days ahead
//   Text       grey     timing in words without a date (e.g. "at flowering", "BBCH 89"), shown as written
//   No date    grey     no due date at all: empty, "?" or "TBD" (as in the old data)
//   Done       green
//   Cancelled  grey, struck through

export const TASK_STATUSES = {
  overdue: {
    label: 'Overdue', pill: 'bg-red-100 text-red-700 dark:bg-red-900/60 dark:text-red-300',
    badge: 'bg-red-600', border: 'border-red-500', surface: 'bg-red-50/50 dark:bg-red-950/20',
  },
  soon: {
    label: 'Due soon', pill: 'bg-orange-100 text-orange-700 dark:bg-orange-900/60 dark:text-orange-300',
    badge: 'bg-orange-600', border: 'border-orange-500', surface: 'bg-orange-50/50 dark:bg-orange-950/20',
  },
  week: {
    label: 'This week', pill: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/60 dark:text-yellow-300',
    badge: 'bg-yellow-500 text-yellow-950', border: 'border-yellow-500', surface: 'bg-yellow-50/50 dark:bg-yellow-950/20',
  },
  later: {
    label: 'Later', pill: 'bg-blue-100 text-blue-700 dark:bg-blue-900/60 dark:text-blue-300',
    badge: 'bg-blue-600', border: 'border-blue-500', surface: 'bg-blue-50/50 dark:bg-blue-950/20',
  },
  text: {
    label: 'Text', pill: 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200', icon: true,
    badge: 'bg-gray-500', border: 'border-gray-400', surface: 'bg-gray-50/50 dark:bg-gray-950/20',
  },
  done: {
    label: 'Done', pill: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/60 dark:text-emerald-300',
    badge: 'bg-emerald-600', border: 'border-emerald-500', surface: 'bg-emerald-50/50 dark:bg-emerald-950/20',
  },
  cancelled: {
    label: 'Cancelled', pill: 'bg-gray-200 text-gray-500 line-through dark:bg-gray-700 dark:text-gray-400',
    badge: 'bg-gray-400 line-through', border: 'border-gray-300', surface: '',
  },
  none: {
    // (light enough to differ from Text's grey, dark enough to show on the dark panels)
    label: 'No date', pill: 'bg-slate-200 text-slate-700 dark:bg-slate-600 dark:text-slate-100',
    badge: 'bg-gray-400', border: 'border-gray-300', surface: 'bg-gray-50/50 dark:bg-gray-950/20',
  },
};

/** The statuses the legend explains, in its order. */
export const LEGEND_STATUSES = ['overdue', 'soon', 'week', 'later', 'text', 'none', 'done', 'cancelled'];

// Time intervals that say only that there is no date yet (customer feedback 1). "CANCELLED" is
// left as it is until the customer has decided.
const NO_DATE_INTERVALS = ['', '?', 'tbd'];

/**
 * A task's Time interval as its two parts (Documentation/plans/timeinterval-text.md in agromate-api): `date`
 * ("YYYY-MM-DD", or '') and `text` - timeinterval_text, else an old text in timeinterval (e.g. "7 DAA"), or ''.
 * The text is words as written; it never gives a date.
 */
export function timeIntervalParts(task) {
  const value = String(task?.timeinterval ?? task?.timeInterval ?? '').trim();
  const date = /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : '';
  const text = String(task?.timeinterval_text ?? '').trim();
  return { date, text: text || (date ? '' : value) };
}

/** Whether a task's Time interval says it is not needed (its text "Not needed"). */
export const isNotNeededInterval = (task) => timeIntervalParts(task).text === 'Not needed';

/** Whether a time interval gives no due date at all (empty, "?" or "TBD"). */
export function hasNoDate(timeinterval) {
  return NO_DATE_INTERVALS.includes(String(timeinterval ?? '').trim().toLowerCase());
}

const startOfDay = (value) => {
  const d = value instanceof Date ? new Date(value) : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  return d;
};

/** Whole days from today to the due date (negative when past); null without a date. */
export function daysUntil(dueDate, today = new Date()) {
  const due = dueDate ? startOfDay(dueDate) : null;
  const now = startOfDay(today);
  if (!due || !now) return null;
  return Math.round((due - now) / (1000 * 60 * 60 * 24));
}

/**
 * The status key of a task (see TASK_STATUSES).
 * @param {{ donedate?: any, cancelled?: any, dueDate?: Date|string|null, dueStart?: Date|string|null, dueEnd?: Date|string|null, timeinterval?: string|null }} task
 *   - an enriched task (taskLogic's enrichTask gives the dueDate and, for a period, its first and last day)
 * @param {Date} [today]
 */
export function getTaskStatus(task, today = new Date()) {
  if (!task) return 'none';
  if (task.cancelled === true || task.cancelled === 1 || task.cancelled === '1') return 'cancelled';
  if (task.donedate) return 'done';
  const days = daysUntil(task.dueStart ?? task.dueDate, today);
  if (days === null) return hasNoDate(task.timeinterval) ? 'none' : 'text';
  const daysToEnd = daysUntil(task.dueEnd, today) ?? days;
  if (daysToEnd < 0) return 'overdue';
  // In the period (a week, a month position, a season) from its first day to its last
  if (days <= 0 && daysToEnd > days) return 'week';
  if (days <= 2) return 'soon';
  if (days <= 7) return 'week';
  return 'later';
}
